import { UIBModalService } from '@b2b/types/core';
import uiRouter, { RawParams, StateService } from '@uirouter/angularjs';
import angular, { IAttributes, ITimeoutService } from 'angular';
import { PageContentStartLoadData, PromptSaveController, PromptSaveScope } from './promptSave.types';

PromptSaveController.$inject = ['$scope', '$uibModal', '$state', '$timeout', '$attrs'];

function PromptSaveController(
  $scope: PromptSaveScope,
  $uibModal: UIBModalService,
  $state: StateService,
  $timeout: ITimeoutService,
  $attrs: IAttributes,
) {

  const ctrl: PromptSaveController = {
    nextState: null,
    nextStateParams: {},
    modalDismissed: false,
    force: false,

    process(state?: string, params?: RawParams) {
      if (state) {
        ctrl.nextState = state;
        ctrl.nextStateParams = params || {};
      }

      if ($scope.checkDirty) {
        const isDirty = ctrl.force || $scope.checkDirty();

        if (isDirty) {
          ctrl.showModal();
        } else {
          ctrl.continueToState();
        }
      } else {
        ctrl.continueToState();
      }
    },

    showModal() {
      const self = $scope.promptSave;
      const modal = $uibModal.open<boolean>({
        templateUrl: 'common/views/modals/save/save.tpl.html',
        size: 'md',
        controller: 'ModalsSaveController',
        resolve: {
          message: () => $scope.saveMessage || 'Would you like to save your changes?',
          dontSaveButtonText: () => $scope.dontSaveButtonText || '',
          saveButtonText: () => $scope.saveButtonText || '',
          cancelButtonText: () => $scope.cancelButtonText || '',
          headingText: () => $scope.headingText || '',
          hideDontSave: () => $scope.hideDontSave === 'true',
        },
      });

      modal.result
        .then((saveAndContinue) => {
          if (saveAndContinue) {
            self.save();
          } else {
            self.dontSave();
          }
        })
        .catch(() => {
          // Modal dismissed - no action needed
        });
    },

    continueToState() {
      ctrl.modalDismissed = true;
      ctrl.force = false;

      if (ctrl.nextState) {
        $state.go(ctrl.nextState, ctrl.nextStateParams);
      }
    },

    dontSave() {
      if ($attrs.dontSave) {
        $scope.dontSave?.();
      } else {
        ctrl.continueToState();
      }
    },

    save() {
      $scope.save?.();
    },

    /**
     * Checks if the given state name matches any of the configured view IDs.
     * @returns The matching view ID, or null if no match found.
     */
    includesView(stateName: string) {
      const list: string[] = $scope.viewIds ? JSON.parse($scope.viewIds) : [];

      return list.find((viewId) => new RegExp(`^${viewId}.*`).test(stateName)) ?? null;
    },
  };

  $scope.promptSave = ctrl;

  $scope.$on('pageContent::startLoad', (_event, data: PageContentStartLoadData) => {
    const ctrl = $scope.promptSave;

    if (!ctrl.modalDismissed && data?.from) {
      const found = ctrl.includesView(data.from.name);

      if (found && !ctrl.includesView(data.to.name)) {
        // Navigating away from a watched view - check dirty state
        if (typeof data.event.abort === 'function') {
          data.event.abort();
        }

        // Use $timeout to ensure we're outside the current digest cycle
        $timeout(() => {
          ctrl.process(data.to.name, data.toParams);
        }, 0);
      }
    }
  });

  $scope.$on('promptSave::Complete', () => {
    $scope.promptSave.continueToState();
  });

  $scope.$on('promptSave::Error', () => {
    // Force modal to show on next navigation attempt
    $scope.promptSave.force = true;
  });
}

function PromptSaveDirective() {
  return {
    restrict: 'A',
    replace: true,
    scope: {
      viewIds: '@',
      checkDirty: '&',
      save: '&',
      saveMessage: '@',
      dontSave: '&',
      dontSaveButtonText: '@',
      hideDontSave: '@',
      saveButtonText: '@',
      cancelButtonText: '@',
      headingText: '@',
    },

    controller: PromptSaveController,
  };
}

export const PromptSaveDirectiveModule = angular
  .module('acme.common.directive.promptSave', [
    uiRouter,
    'ui.bootstrap',
    'acme.view.application.modals.save.controllers',
  ])
  .directive('promptSave', PromptSaveDirective).name;
